import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { usePostAdminTenants } from '@/__generated__/admin/admin';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { isAxiosError } from 'axios';

const formSchema = z.object({
  subdomain: z.string().min(1, 'サブドメインを入力してください'),
  display_name: z.string().min(1, '表示名を入力してください'),
  disable_ai_compass: z.boolean().optional(),
  disable_operator: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export function CreateTenantDialog({ onSuccess }: { onSuccess: () => void }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      subdomain: '',
      display_name: '',
      disable_ai_compass: false,
      disable_operator: false,
    },
  });

  const { trigger: createTenant, reset } = usePostAdminTenants();

  const onSubmit = async (values: FormValues) => {
    try {
      await createTenant({
        subdomain: values.subdomain,
        display_name: values.display_name,
        disable_ai_compass: values.disable_ai_compass,
        disable_operator: values.disable_operator,
      });
      setIsOpen(false);
      toast({
        title: 'テナントを作成しました',
        description: 'テナントの作成が完了しました',
        variant: 'default',
      });
      reset();
      form.reset();
      onSuccess();
    } catch (error) {
      if (isAxiosError(error)) {
        toast({
          title: 'テナント作成に失敗しました',
          description: error.response?.data.message,
          variant: 'destructive',
        });
      }
      console.error('Failed to create tenant:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>テナント作成</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>テナント作成</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="subdomain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>サブドメイン</FormLabel>
                  <FormControl>
                    <Input placeholder="サブドメインを入力" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>表示名</FormLabel>
                  <FormControl>
                    <Input placeholder="表示名を入力" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="disable_ai_compass"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>AI Compassを無効化</FormLabel>
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="disable_operator"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>オペレーター機能を無効化</FormLabel>
                  </div>
                </FormItem>
              )}
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? '作成中...' : '作成'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
